import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  Draggable,
  DraggableProvided,
  DraggableStateSnapshot,
} from 'react-beautiful-dnd';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { get } from 'lodash';
import {
  Icon,
  Tooltip,
  IconButton,
  decimalHoursToTimeString,
  tailwindOverride,
} from '@getsynapse/design-system';
import intl from 'react-intl-universal';
import { TaskTemplate } from 'utils/types/taskTemplate';
import { getTaskType } from 'utils/tasks';
import { TASK_TYPE_CATEGORY } from 'utils/constants';
import { selectTaskTypes } from 'state/TasksList/allTasks/allTasksSlice';
import { useTaskBundleDraft } from '../../providers/TaskBundleProvider/TaskBundleProvider';
import DragAndDropHandler from 'assets/icons/drag-and-drop-handler.svg';
import TaskTemplatePredecessorsColumn from './TaskTemplatePredecessorsColumn';
import TaskTemplateTimelineCell from './TaskTemplateTimelineCell';

interface TaskTemplateRowProps {
  taskTemplate: TaskTemplate;
  index: number;
  isOdd: boolean;
  onEdit: (task: TaskTemplate) => void;
  onDelete: (taskTemplateId: string) => void;
}

const TaskTemplateRow: React.FC<TaskTemplateRowProps> = ({
  taskTemplate,
  index,
  isOdd,
  onEdit,
  onDelete,
}) => {
  const {
    flexibleTaskTimeEntry,
    newTaskTypes = false,
    taskDependenciesInBundles = false,
    relativeDates = false,
  } = useFlags();
  const showDependencies = newTaskTypes && taskDependenciesInBundles;
  const showTimeline = newTaskTypes && relativeDates;
  const taskTypes = useSelector(selectTaskTypes);
  const { selectors } = useTaskBundleDraft();
  const dependencies = selectors.getTemplateDependencies(taskTemplate.id);

  const rowBg = isOdd ? 'bg-neutral-lightest-two' : 'bg-neutral-white';

  const getTaskTypeContent = useCallback(() => {
    const type = taskTemplate.type || '';
    const fromState = taskTypes[type];
    if (fromState) return fromState;
    const translated = getTaskType(type, TASK_TYPE_CATEGORY.TABLE);
    if (translated) return translated;
    if (type) return type;
    /* eslint-disable-next-line no-console */
    console.warn(`Task template "${taskTemplate.name}" has no task type`);
    return '-';
  }, [taskTemplate.type, taskTemplate.name, taskTypes]);

  const getEstimatedHours = useCallback(
    (estimatedHours: string) => {
      if (!estimatedHours) return '-';
      if (flexibleTaskTimeEntry)
        return decimalHoursToTimeString(parseFloat(estimatedHours));
      return estimatedHours;
    },
    [flexibleTaskTimeEntry]
  );

  return (
    <Draggable
      key={taskTemplate.id}
      draggableId={taskTemplate.id}
      index={index}
    >
      {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => {
        const cellBorderAndShadow = snapshot.isDragging
          ? 'border-secondary-darker shadow-table-column'
          : 'border-transparent shadow-table-column group-hover:border-secondary-darker group-hover:shadow-none';
        const cellBase = tailwindOverride(
          'border-b border-t border-r-0 px-4 py-3',
          cellBorderAndShadow
        );
        const dragHandleCellBase = tailwindOverride(
          'border-b border-t border-r-0 p-0',
          cellBorderAndShadow
        );
        return (
          <tr
            ref={provided.innerRef}
            data-testid={`table-row-${index}`}
            {...provided.draggableProps}
            className={tailwindOverride(
              'text-label text-primary group hover:shadow-lifted cursor-pointer',
              rowBg,
              { 'shadow-lifted': snapshot.isDragging }
            )}
            onClick={() => onEdit(taskTemplate)}
          >
            {newTaskTypes && (
              <td
                className={tailwindOverride(
                  dragHandleCellBase,
                  'h-full w-6',
                  snapshot.isDragging
                    ? 'shadow-first-table-cell-hover'
                    : 'group-hover:shadow-first-table-cell-hover'
                )}
              >
                <div
                  className={tailwindOverride(
                    'w-6 flex items-center justify-center',
                    snapshot.isDragging
                      ? 'opacity-100'
                      : 'opacity-0 group-hover:opacity-100'
                  )}
                  {...provided.dragHandleProps}
                >
                  <Icon
                    src={DragAndDropHandler}
                    className='text-xl text-neutral-dark'
                  />
                </div>
              </td>
            )}
            <td
              className={tailwindOverride(cellBase, 'w-5/12 max-w-94 truncate')}
            >
              <div
                className='w-full flex items-center'
                title={get(taskTemplate, 'name') || '-'}
              >
                {taskTemplate.name}
              </div>
            </td>
            <td className={tailwindOverride(cellBase, 'w-7/12 max-w-94')}>
              <div
                className='w-full line-clamp-2 whitespace-normal'
                title={get(taskTemplate, 'description') || '-'}
              >
                {get(taskTemplate, 'description') || '-'}
              </div>
            </td>
            <td
              className={tailwindOverride(cellBase, 'w-46 max-w-46 truncate')}
            >
              <div className='w-46 truncate'>{getTaskTypeContent()}</div>
            </td>
            {showDependencies && (
              <td
                className={tailwindOverride(cellBase, 'w-52 max-w-52 truncate')}
              >
                <TaskTemplatePredecessorsColumn dependencies={dependencies} />
              </td>
            )}
            <td
              className={tailwindOverride(cellBase, 'w-28 max-w-28 truncate')}
            >
              <div className='w-28 truncate'>
                {getEstimatedHours(taskTemplate.estimated_hours ?? '')}
              </div>
            </td>
            {showTimeline && (
              <td className={tailwindOverride(cellBase, 'w-52 max-w-52')}>
                <TaskTemplateTimelineCell
                  startDay={taskTemplate.start_day}
                  duration={taskTemplate.duration}
                />
              </td>
            )}
            <td
              className={tailwindOverride(
                cellBase,
                'border-r w-16 right-0 sticky z-1',
                rowBg
              )}
              onClick={(event) => event.stopPropagation()}
            >
              <div className='flex w-full justify-center'>
                <Tooltip
                  openMode='hover1'
                  timeout={0}
                  showPopper
                  position='topCenter'
                  contentProps={{
                    className: 'px-3 py-2 text-sm z-50',
                  }}
                  usePortal
                  trigger={
                    <IconButton
                      name='trash'
                      iconClassname='pointer-events-none'
                      className='text-base ml-2 text-neutral-dark p-1 hover:bg-neutral-lightest hover:shadow-allocation-table rounded'
                      onClick={() => onDelete(taskTemplate.id)}
                      data-cy={`remove-task-${taskTemplate.id}_button`}
                      data-testid={`remove-task-${taskTemplate.id}`}
                    />
                  }
                >
                  {intl.get('REMOVE')}
                </Tooltip>
              </div>
            </td>
          </tr>
        );
      }}
    </Draggable>
  );
};

export default TaskTemplateRow;
